<template>
  <div class="account-details">
    <div class="row">
      <div class="col-12 col-md-8 border-right">
        <form-control
          v-model="user.first_name"
          :errors="error ? error.errors.first_name : []"
          title="First Name"
        />

        <form-control
          v-model="user.last_name"
          :errors="error ? error.errors.last_name : []"
          title="Last Name"
        />

        <form-control
          v-model="user.email"
          :errors="error ? error.errors.email : []"
          title="Email"
        />

        <div class="form-group">
          <label>Mobile Phone</label>
          <tel-input
            :class="{
              error: !mobileValid && user.phone,
              'is-invalid':
                (error && error.errors.phone ? error.errors.phone : []).length >
                  0 && user.phone,
            }"
            v-model="user.phone"
            @validate="mobileValidStateChange"
          />
          <form-control-error
            :errors="error && error.errors.phone ? error.errors.phone : []"
          />
        </div>

        <div class="form-group">
          <label>Work Phone</label>
          <tel-input
            :class="{
              error: !workValid && user.work_phone,
              'is-invalid':
                (error && error.errors.work_phone
                  ? error.errors.work_phone
                  : []
                ).length > 0 && user.work_phone,
            }"
            v-model="user.work_phone"
            @validate="workValidStateChange"
          />
          <form-control-error
            :errors="
              error && error.errors.work_phone ? error.errors.work_phone : []
            "
          />
        </div>

        <form-control
          v-model="user.city"
          :errors="error ? error.errors.city : []"
          title="City"
        />

        <div class="form-group">
          <label>Country</label>
          <multiselect
            v-model="user.country"
            :options="countries"
            :show-labels="false"
            :class="{
              'is-invalid':
                (error && error.errors.country_id
                  ? error.errors.country_id
                  : []
                ).length > 0,
            }"
            placeholder="Type to search"
            track-by="id"
            label="name"
            @select="(val) => (user.country_id = val.id)"
          >
            <span slot="noResult"> No elements found. </span>
            <span slot="placeholder" class="multiselect__placeholder">
              Type to search
            </span>
          </multiselect>
        </div>

        <div class="form-group">
          <label>Role</label>
          <multiselect
            v-model="user.user_role"
            :options="userRoles"
            :show-labels="false"
            :class="{
              'is-invalid':
                (error && error.errors.user_role_id
                  ? error.errors.user_role_id
                  : []
                ).length > 0,
            }"
            placeholder="Type to search"
            track-by="id"
            label="name"
            @select="(val) => (user.user_role_id = val.id)"
          >
            <span slot="noResult"> No elements found. </span>
            <span slot="placeholder" class="multiselect__placeholder">
              Type to search
            </span>
          </multiselect>
        </div>

        <div class="form-group py-2 alert-lists">
          <div class="d-flex align-items-center">
            <label>Alert Lists</label>
            <div
              v-if="newAlertLists.length > 0"
              class="alert-list d-table-flex ml-auto slot-wrapper clear-all-alerts"
              @click="newAlertLists = []"
            >
              <span class="btn"> Clear All Alerts </span>
              <icon icon="tag-remove" />
            </div>
          </div>
          <quick-selector
            v-model="newAlertLists"
            :options="allAlertLists"
            :selected="alertLists"
            :class="{
              'is-invalid':
                (error && error.errors.alertLists
                  ? error.errors.alertLists
                  : []
                ).length > 0,
            }"
            class="quick-selection--authors my-1"
            placeholder="Add an alert list"
          >
            <template slot="option" slot-scope="scope" v-if="scope.option">
              <span>{{ scope.option.name }}</span>
            </template>
          </quick-selector>

          <div v-if="newAlertLists.length > 0" class="tag-input">
            <div class="tag-input__list">
              <div
                v-for="(alertList, index) in newAlertLists"
                :key="alertList.id"
                class="tag-input__list__item"
              >
                <span>{{ alertList.name }}</span>
                <span @click="newAlertLists.splice(index, 1)">
                  <icon icon="remove-tag" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="d-flex flex-column subscriptions">
            <label> Subscriptions </label>
            <div class="tag-input" v-if="user.all_subscriptions.length > 0">
              <div class="tag-input__list">
                <router-link
                  v-for="sub in user.all_subscriptions"
                  :key="sub.id"
                  :to="`/subscriptions/${sub.id}`"
                  class="tag-input__list__item link"
                  :class="{ inactive: sub.status === 'Expired' }"
                >
                  <span>{{ sub.name }}</span>
                </router-link>
              </div>
            </div>
            <span v-else> &mdash; </span>
          </div>
          <div
            v-if="$login.can('roles.advanced-permissions')"
            class="form-group mt-3"
          >
            <label>Roles</label>
            <multiselect
              :options="roles"
              v-model="user.roles"
              :allow-empty="true"
              :searchable="true"
              :multiple="true"
              :show-labels="false"
              label="name"
              track-by="id"
            />
          </div>
          <div class="form-group mt-3">
            <div class="label">Permissions</div>
            <entitlements
              v-if="user.permissions.length > 0"
              :permissions="user.permissions"
              class="mt-3"
            />
            <div class="small font-italic">
              Note that editors, analysts and admins may have different
              permissions than his/her subscriptions indicate
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Account</label>
          <multiselect
            :options="accounts"
            v-model="user.account"
            class="mr-3"
            :internal-search="false"
            :allow-empty="true"
            :searchable="true"
            :multiple="false"
            :show-labels="false"
            label="name"
            track-by="id"
            @search-change="delayGetAccounts"
            @select="changedAccount"
          />
          <div v-if="user.account_id" class="d-flex">
            <router-link
              :to="{
                name: 'accounts.view',
                params: { accountId: user.account_id },
              }"
              class="small"
            >
              <icon icon="preview" /> View account
            </router-link>

            <a
              @click="changedAccount"
              class="small text-primary ml-2 d-block"
              href="javascript:;"
            >
              <icon icon="remove" />Remove account
            </a>
          </div>
        </div>

        <div class="form-group">
          <label>Channels</label>
          <multiselect
            :options="channels"
            v-model="user.channels"
            :internal-search="false"
            :allow-empty="true"
            :searchable="true"
            :multiple="true"
            :show-labels="false"
            label="name"
            track-by="id"
            @search-change="delayGetChannels"
          />
        </div>
      </div>

      <div class="col-12 col-md-4">
        <div class="form-group">
          <label>Profile picture</label>
          <img width="64" class="d-block mb-3" :src="user.picture_url" />
          <FileUpload
            :accept="['image/png', 'image/jpeg']"
            :headers="headers()"
            :value="
              user.picture_url
                ? [{ name: user.picture_url, url: user.picture_url }]
                : null
            "
            @input="pictureUploaded"
            :action="url() + 'users/picture'"
            :data="{ params: { picture_type: 'round', user_name: user.name } }"
          />
        </div>

        <div class="form-group">
          <label>Square profile picture</label>
          <img width="64" class="d-block mb-3" :src="user.picture_sq_url" />
          <FileUpload
            :accept="['image/png', 'image/jpeg']"
            :headers="headers()"
            :value="
              user.picture_sq_url
                ? [{ name: user.picture_sq_url, url: user.picture_sq_url }]
                : null
            "
            @input="pictureSqUploaded"
            :action="url() + 'users/picture'"
            :data="{ params: { picture_type: 'square', user_name: user.name } }"
          />
        </div>
        <div class="form-group d-flex flex-column">
          <label>GDPR</label>
          <div class="w-100 account-details__gdpr">
            <button
              class="btn btn-outline-secondary"
              :class="{ 'btn-outline-primary': user.is_gdpr_compliant }"
            >
              Compliant
            </button>
            <button
              class="btn btn-outline-secondary"
              :class="{ 'btn-outline-primary': !user.is_gdpr_compliant }"
            >
              Not Compliant
            </button>
          </div>
        </div>

        <div class="form-group d-flex flex-column">
          <label>Interests: Market Cap</label>
          <Dropdown v-model="user.interests">
            <DropdownOption
              v-for="item in [
                { label: 'Large (more than 1b)', value: 'large' },
              ]"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </DropdownOption>
          </Dropdown>
        </div>
        <div class="form-group d-flex flex-column">
          <label>Email preferences</label>
          <t-checkbox v-model="user.is_opted_out">
            Opt out of all emails
          </t-checkbox>
        </div>

        <div class="form-group">
          <label>Active packages</label>
          <div class="tag-input py-0" v-if="user.all_roles.length > 0">
            <div class="tag-input__list">
              <router-link
                v-for="role in user.all_roles"
                :key="role.id"
                :to="`/packages/${role.id}`"
                class="tag-input__list__item link"
              >
                <span>{{ role.name }}</span>
              </router-link>
            </div>
          </div>
          <div v-else>None</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuickSelector from '@/components/QuickSelector'
import { successNotification, errorNotification } from '@/notifications'
import { debounce, get, getOnce } from '@/helpers'
import Entitlements from '@/components/Entitlements'
import { VueTelInput as TelInput } from 'vue-tel-input'
import Dropdown from '@/components/Dropdown/Dropdown'
import gateway from '@/gateway'
import DropdownOption from '@/components/Dropdown/DropdownOption'
import { FileUpload } from '@frontend/components'

export default {
  components: {
    QuickSelector,
    Entitlements,
    TelInput,
    DropdownOption,
    FileUpload,
    Dropdown,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },
    alertLists: {
      type: Array,
      required: true,
    },
    allAlertLists: {
      type: Array,
      required: true,
    },
  },

  mounted() {
    this.getRoles()
    this.getAccounts()
    this.getChannels()
    getOnce('countries').then((r) => (this.countries = r.data.data))
    getOnce('user-roles').then((r) => (this.userRoles = r.data.data))
  },

  watch: {
    alertLists() {
      this.newAlertLists = this.alertLists.slice(0)
    },
    user() {
      this.data = this.user
      this.picture = this.user.picture_url
        ? [
            {
              name: this.user.picture_url,
              url: this.user.picture_url,
            },
          ]
        : []
      this.pictureSq = this.user.picture_sq_url
        ? [
            {
              name: this.user.picture_sq_url,
              url: this.user.picture_sq_url,
            },
          ]
        : []
    },
  },

  data() {
    return {
      error: null,
      picture: [],
      pictureSq: [],
      newAlertLists: this.alertLists.slice(0),
      accounts: [],
      channels: [],
      userRoles: [],
      roles: [],
      countries: [],
      data: this.user,
      mobileValid: false,
      workValid: false,
    }
  },

  methods: {
    headers() {
      return {
        Authorization: 'Bearer ' + this.$login.getToken(),
      }
    },

    url() {
      return process.env.VUE_APP_API_ENDPOINT
    },

    pictureUploaded(r) {
      const response = r[0]
      this.user.picture = response.name
      this.user.picture_url = response.url
    },

    pictureSqUploaded(r) {
      const response = r[0]

      this.user.picture_sq = response.name
      this.user.picture_sq_url = response.url
    },

    mobileValidStateChange({ isValid, number, country }) {
      this.mobileValid = isValid
    },

    workValidStateChange({ isValid, number, country }) {
      this.workValid = isValid
    },

    async createFollows(userId) {
      const url = `crm/follows`
      const data = {
        includedTags: [],
            excludedTags: [],
            receiveEmailNotifications: false,
            receiveDesktopNotifications: false,
            receiveWhatsappNotifications: false,
            userId,
      }

      try {
        const responses = await Promise.all([
          gateway.post(url, {
            tagId: 106737,
            ...data,
          }),
          gateway.post(url, {
            tagId: 13700,
            ...data,
          }),
        ])
        console.log(data, responses)
      } catch (e) {
        console.log(e)
      }
    },

    create() {
      this.$http
        .post(`/users`, this.user)
        .then(async (response) => {
          await Promise.all([
            this.$http.put(`/users/${response.data.data.id}/alert-lists`, {
              alert_lists: this.newAlertLists,
            }),
            this.createFollows(response.data.data.id),
          ])

          this.$emit('save-success', response)
          this.error = null
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            this.error = error.response.data
          }
          this.$emit('save-fail', error)
        })
    },

    save() {
      this.error = null
      if (!this.mobileValid && this.user.phone) {
        this.error = {
          message: 'The given data was invalid.',
          errors: { phone: ['This phone number is not valid.'] },
        }
      }
      if (!this.workValid && this.user.work_phone) {
        if (this.error) {
          this.error.errors.work_phone = ['This phone number is not valid.']
        } else {
          this.error = {
            message: 'The given data was invalid.',
            errors: { work_phone: ['This phone number is not valid.'] },
          }
        }
      }
      if (!this.user.country_id) {
        if (this.error) {
          this.error.errors.country_id = ['Country is required.']
        } else {
          this.error = {
            message: 'The given data was invalid.',
            errors: { country_id: ['Country is required.'] },
          }
        }
      }
      if (this.error) {
        const errors = this.error.errors
        let errorStr = ''
        for (const key in errors) {
          if (errors[key]) {
            errorStr +=
              errors && errors[key]
                ? '<li>' +
                  (typeof errors[key] === 'string'
                    ? errors[key]
                    : errors[key][0]) +
                  '</li>'
                : ''
          }
        }

        return this.$notify(errorNotification(`</br><ul>${errorStr}</ul>`))
      }
      if (!this.user.id) {
        return this.create()
      }
      Promise.all([
        this.$http.put(`/users/${this.user.id}`, this.user),
        this.$http.put(`/users/${this.user.id}/alert-lists`, {
          alert_lists: this.newAlertLists,
        }),
      ])
        .then((response) => {
          this.$notify(successNotification('Saved user successfully'))
          this.$emit('save-success', response)
          this.error = null
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            this.error = error.response.data
          }
          this.$emit('save-fail', error)
        })
    },

    delayGetAccounts: debounce(function (search) {
      this.getAccounts(search)
    }, 750),

    getAccounts(search = null) {
      get('accounts', { search }).then(this.gotAccounts)
    },

    gotAccounts({ data: { data } }) {
      this.accounts = data
    },

    delayGetChannels: debounce(function (search) {
      this.getChannels(search)
    }, 750),

    getChannels(search = null) {
      get('channels', { search }).then(this.gotChannels)
    },

    async getRoles() {
      const { data } = await getOnce('roles')

      this.roles = data.data
    },

    gotChannels({ data: { data } }) {
      this.channels = data
    },

    changedAccount(option = null) {
      if (option.id) {
        this.user.account_id = option.id
        this.user.account = option
      } else {
        this.user.account = null
        this.user.account_id = null
      }
    },
  },
}
</script>

<style lang="scss">
.account-details {
  .alert-lists .tag-input {
    padding-bottom: 0;
  }
  .subscriptions .tag-input {
    padding: 0;
  }
}
</style>
